"use client";

import { useEffect, useMemo, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useCallStore } from "@/store/call-store";
import { clock, cn } from "@/lib/utils";
import { TerminalBootSequence } from "@/components/ui/terminal-boot";

export function LiveTranscript({ className }: { className?: string }) {
  const transcript = useCallStore((s) => s.transcript);
  const scroller = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = scroller.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [transcript.length]);

  return (
    <div className={cn("flex min-h-0 flex-col bg-card card overflow-hidden", className)}>
      <header className="flex h-12 items-center gap-2 border-b border-line px-4 bg-card-muted/50">
        <h2 className="text-sm font-semibold">Live Transcript</h2>
      </header>

      <div
        ref={scroller}
        className="scroll-thin min-h-0 flex-1 overflow-y-auto p-4"
      >
        {transcript.length === 0 && (
          <TerminalBootSequence 
            lines={[
              "varta listen --source=mic",
              "[sys] MOUNTING AUDIO DRIVER... OK",
              "[sys] LOADING SPEECH RECOGNITION ENGINES... 100%",
              "[sys] ESTABLISHING SECURE SOCKETS... OK",
              "AWAITING AUDIO STREAM..."
            ]} 
            promptColor="text-success"
          />
        )}

        <AnimatePresence initial={false}>
          {transcript.map((line) => {
            const isClient = line.speaker === "customer";
            return (
              <motion.div
                key={line.id}
                layout
                initial={{ opacity: 0, y: 8, filter: 'blur(8px)', scale: 0.98 }}
                animate={{ opacity: 1, y: 0, filter: 'blur(0px)', scale: 1 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
                className={cn(
                  "group relative mb-5 flex gap-3",
                  isClient ? "" : "flex-row-reverse"
                )}
              >
                <div 
                  className={cn(
                    "grid size-8 shrink-0 place-items-center rounded-full text-xs font-bold text-bg shadow-sm mt-0.5",
                    isClient ? "bg-purple" : "bg-primary"
                  )}
                >
                  {line.name.substring(0, 2).toUpperCase()}
                </div>
                
                <div className={cn("flex flex-col max-w-[85%]", isClient ? "items-start" : "items-end")}>
                  <div className="mb-1 flex items-center gap-2 px-1">
                    <span className="text-xs font-semibold text-text-secondary">{line.name}</span>
                    <span className="tnum text-[10px] text-text-tertiary">
                      {clock(line.at)}
                    </span>
                  </div>
                  <div 
                    className={cn(
                      "px-4 py-2.5 rounded-2xl text-[14px] leading-relaxed shadow-sm",
                      isClient 
                        ? "bg-card-muted border border-line rounded-tl-sm text-text" 
                        : "bg-primary text-bg rounded-tr-sm"
                    )}
                  >
                    {line.text}
                  </div>
                </div>
              </motion.div>
            )
          })}
        </AnimatePresence>
      </div>
    </div>
  );
}
